import { createRouter, createWebHistory } from 'vue-router'

import { Module } from '@/enums/module'
import { landingRoute, routeAllowed } from '@/libs/access'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

/*
 * What a route says about itself. `module` — the portal module it belongs to, which the person
 * must have; `requiresAdmin` — a portal administrator's page (docs/API.md → Permissions).
 */
declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?:  boolean
        requiresAdmin?: boolean
        module?:        Module
        layout?:        'Default' | 'Blank'
    }
}

/*
 * The signed-in shell is exactly the viewport (`layouts/Default.vue`, `h-dvh`) and its `<main>`
 * is what scrolls — the window never does. So a navigation to another page scrolls THAT
 * element back to the top; a change of the query only (a tab, a filter, the task drawer's
 * `?task=`) leaves it where it is.
 */
const shellMain = (): HTMLElement | null => document.querySelector<HTMLElement>('main')

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        const main = shellMain()

        if (!main) {
            if (savedPosition)         return savedPosition
            if (from.path === to.path) return {}

            return { top: 0 }
        }

        if (from.path !== to.path) main.scrollTo({ top: 0 })

        return false
    },
    routes: [
        // The landing depends on the person's modules: Naloge, else the profile.
        {
            path:     '/',
            name:     'landing',
            redirect: () => landingRoute(),
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        // Naloge's landing page: what is mine and late, due today, due this week.
        {
            path:      '/dashboard',
            name:      'dashboard',
            component: () => import('@/views/dashboard/Dashboard.vue'),
            meta:      { requiresAuth: true, module: Module.Naloge },
        },
        {
            path:      '/my-tasks',
            name:      'my-tasks',
            component: () => import('@/views/task/MyTasks.vue'),
            meta:      { requiresAuth: true, module: Module.Naloge },
        },
        {
            path:      '/projects',
            name:      'projects',
            component: () => import('@/views/project/Projects.vue'),
            meta:      { requiresAuth: true, module: Module.Naloge },
        },
        // `?view=` picks the tab, `?task=` opens a task — the address every e-mail links to.
        {
            path:      '/projects/:id',
            name:      'project',
            component: () => import('@/views/project/Project.vue'),
            meta:      { requiresAuth: true, module: Module.Naloge },
        },
        // Who must do what by when: every person's tasks on one calendar.
        {
            path:      '/timeline',
            name:      'timeline',
            component: () => import('@/views/timeline/Timeline.vue'),
            meta:      { requiresAuth: true, module: Module.Naloge },
        },
        {
            path:      '/users',
            name:      'users',
            component: () => import('@/views/user/Users.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        {
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        // Everybody's own account: name, e-mail, notifications, password, passkeys, access.
        {
            path:      '/profile',
            name:      'profile',
            component: () => import('@/views/profile/Profile.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:     '/:pathMatch(.*)*',
            redirect: () => landingRoute(),
        },
    ],
})

router.beforeEach((to, _from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    if (loggedIn && to.name === 'login') return next(landingRoute())

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        // The address comes back after signing in — an e-mail's link to a task included.
        return next({ name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : {} })
    }

    /*
     * A page of a module the person does not have, or an administrators-only one. The modules
     * arrive with GET /me, which App.vue asks for as the console starts, so on a cold load they
     * are not known here yet — and a guard that guessed would send somebody away from their own
     * page. It refuses only what it can tell is not theirs (`routeAllowed` says yes until /me
     * is in); App.vue makes the same check once the answer is there, before drawing the page.
     */
    if (!routeAllowed(to.meta)) return next(landingRoute())

    next()
})

// A redeploy replaces the hashed chunk files while a browser may still hold the old
// index.html; every route is lazy, so reload once when a chunk cannot be loaded.
router.onError((error) => {
    if (/dynamically imported module|Importing a module script failed|Failed to fetch/i.test(String(error))) {
        window.location.reload()
    }
})

export default router
